import React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { 
  Star, 
  TrendingUp, 
  TrendingDown, 
  MapPin, 
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  BarChart3
} from 'lucide-react'
import { fetchEnhancedReviewStats } from '@/utils/enhancedReviewsApi'
import { useQuery } from '@tanstack/react-query'

export default function EnhancedReviewStats() {
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['enhanced-review-stats'],
    queryFn: fetchEnhancedReviewStats,
    staleTime: 1000 * 60 * 10, // 10 minutes
  })

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(8)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="space-y-0 pb-2">
              <div className="h-4 bg-muted animate-pulse rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-muted animate-pulse rounded mb-2" />
              <div className="h-3 bg-muted animate-pulse rounded w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (error || !stats) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <div className="text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto" />
            <p className="text-muted-foreground">Failed to load enhanced statistics</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving':
      case 'increasing':
        return <TrendingUp className="w-4 h-4 text-green-600" />
      case 'declining':
      case 'decreasing':
        return <TrendingDown className="w-4 h-4 text-red-600" />
      default:
        return <BarChart3 className="w-4 h-4 text-blue-600" />
    }
  }

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'improving':
      case 'increasing':
        return 'text-green-600'
      case 'declining':
      case 'decreasing':
        return 'text-red-600'
      default:
        return 'text-blue-600'
    }
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Overview Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Review Velocity</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.overall_performance.review_velocity_30d}</div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              {stats.overall_performance.review_velocity_change >= 0 ? (
                <TrendingUp className="w-3 h-3 text-green-600" />
              ) : (
                <TrendingDown className="w-3 h-3 text-red-600" />
              )}
              <span className={stats.overall_performance.review_velocity_change >= 0 ? 'text-green-600' : 'text-red-600'}>
                {stats.overall_performance.review_velocity_change >= 0 ? '+' : ''}{stats.overall_performance.review_velocity_change}%
              </span>
              this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rating Trend</CardTitle>
            {getTrendIcon(stats.performance_insights.recent_rating_trends)}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-1">
              {stats.overall_performance.average_rating}
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star} 
                    className={`w-4 h-4 ${star <= Math.floor(stats.overall_performance.average_rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                  />
                ))}
              </div>
            </div>
            <p className={`text-xs ${getTrendColor(stats.performance_insights.recent_rating_trends)} capitalize`}>
              {stats.performance_insights.recent_rating_trends} trend
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Response Backlog</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.time_analysis.response_time_analysis.overdue_responses}</div>
            <p className="text-xs text-muted-foreground">
              {stats.overall_performance.response_rate}% response rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Location</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.performance_insights.top_performing_locations[0] || 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              Highest rated location
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Rating Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Rating Distribution</CardTitle>
          <CardDescription>Breakdown of all reviews by star rating</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { rating: '5 Star', data: stats.rating_distribution.five_star, color: 'bg-green-500' },
              { rating: '4 Star', data: stats.rating_distribution.four_star, color: 'bg-blue-500' },
              { rating: '3 Star', data: stats.rating_distribution.three_star, color: 'bg-yellow-500' },
              { rating: '2 Star', data: stats.rating_distribution.two_star, color: 'bg-orange-500' },
              { rating: '1 Star', data: stats.rating_distribution.one_star, color: 'bg-red-500' }
            ].map(({ rating, data }) => (
              <div key={rating} className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 w-16">
                  <span className="text-sm font-medium">{rating}</span>
                </div>
                <div className="flex-1">
                  <Progress value={data.percentage} className="h-2" />
                </div>
                <div className="flex items-center space-x-2 w-20 text-right">
                  <span className="text-sm font-medium">{data.count}</span>
                  <span className="text-xs text-muted-foreground">({data.percentage.toFixed(1)}%)</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Location Performance */}
      <Card>
        <CardHeader>
          <CardTitle>Location Performance</CardTitle>
          <CardDescription>Review metrics by franchise location</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {stats.location_breakdown.map((location) => (
              <div key={location.location_name} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">{location.location_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {location.total_reviews} reviews • {location.average_rating} avg rating
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <div className="text-sm font-medium text-green-600">{location.five_star}</div>
                    <div className="text-xs text-muted-foreground">5★</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium text-blue-600">{location.four_star}</div>
                    <div className="text-xs text-muted-foreground">4★</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium text-orange-600">{location.one_star}</div>
                    <div className="text-xs text-muted-foreground">1★</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium">{location.response_rate}%</div>
                    <div className="text-xs text-muted-foreground">Response</div>
                  </div>
                  
                  {stats.performance_insights.locations_needing_attention.includes(location.location_name) ? (
                    <Badge variant="destructive" className="text-xs">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Needs Attention
                    </Badge>
                  ) : stats.performance_insights.top_performing_locations.includes(location.location_name) ? (
                    <Badge variant="secondary" className="text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Top Performer
                    </Badge>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Monthly Trends */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Review Trends</CardTitle>
          <CardDescription>Review volume and ratings over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {stats.time_analysis.reviews_by_month.slice(-12).map((month) => (
              <div key={month.period} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="font-medium">{month.period}</div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <div className="text-sm font-medium">{month.review_count}</div>
                    <div className="text-xs text-muted-foreground">Reviews</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium">{month.average_rating.toFixed(1)}</div>
                    <div className="text-xs text-muted-foreground">Avg Rating</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-medium">{month.response_rate.toFixed(0)}%</div>
                    <div className="text-xs text-muted-foreground">Response Rate</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}